import { Grid, Stack } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { GItem, GItemContainer, MiddleGItem } from '../components/common/grids'
import { SubTitle } from '../components/common/subTitle'
import { AttentionLabel } from '../components/common/attentionLabel'
import { ErrorMessageCard } from '../components/common/errorMessageCard'
import { BottomButtonGroup } from '../components/common/bottomButtonGroup'
import { ButtonOutlinedWarningL } from '../components/common/buttons/buttonOutlinedWarningL'
import { BackButton } from '../components/common/buttons/backButton'
import { Form } from '../components/common/inputs/form'
import { TextBox } from '../components/common/inputs/textBox'
import { RadioButtonGroup } from '../components/common/inputs/radioButtonGroup'
import { TextBoxAfterText } from '../components/common/inputs/textBoxAfterText'
import { CheckBox } from '../components/common/inputs/checkBox'
import { translate } from '../../i18n'
import { yesNo } from '../../containers/common/constant/classification'
import { GetCategoryMstDto, AddQuestion } from '../../dataAccess/webApi/dto/categoryMstDto'
import { useAction } from '../../containers/memberModifyChildDevelopment/memberModifyChildDevelopmentService'

const Flag = { ON: '1', OFF: '0' }

export const MemberModifyChildDevelopment = () => {
  const {
    CATEGORY_KEY,
    formMethods,
    categoryMst,
    onSubmit,
    childrenResultCode,
  } = useAction()
  
  const targetCategories = categoryMst?.filter((value) => CATEGORY_KEY.includes(value.category))
  
  return (
    <Form formMethods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Grid container>
          <Grid item md={3}></Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {targetCategories != null && targetCategories.length > 0 ?
                (<>
                  <SubTitle title={'発達'} />
                  {
                    targetCategories.map((mst) => {
                      switch(mst?.category){
                        case "developmentStage":
                          return (
                            <DevelopmentStageInputForm
                              categoryName="developmentStage"
                              categoryMst={mst}
                            />
                          )
                        case "development.changingClothes":
                          return (
                            <ChangingClothesInputForm 
                              categoryMst={mst}
                            />
                          )
                        case "development.words":
                        case "development.personality":
                        case "development.schoolBehaviour": 
                        case "development.careNote":
                          return (
                            <GItemContainer xs={12} rowSpacing={1}>
                              <MiddleGItem xs={5} sm={2}>
                                <GItemContainer xs={12}>
                                  <GItem xs={12}>
                                    <div>
                                      {mst.required === yesNo.yes && (
                                        <AttentionLabel>*</AttentionLabel>
                                      )}
                                      {mst.name}
                                    </div>
                                  </GItem>
                                </GItemContainer>
                              </MiddleGItem>
                              <GItem xs={7} sm={10}>
                                <TextBox
                                  label={mst.name}
                                  name={mst.category}
                                  maxLength={mst.maxLength}
                                  rows={1}
                                  required={mst.required === yesNo.yes}
                                  multiline
                                  fullWidth
                                />
                              </GItem>
                            </GItemContainer>
                          )                        
                        default:
                          return null
                      }
                    })
                  }
                </>)
                :
                (<>
                  {translate('memberModifyChildDevelopment.error.noCategoryMst')}
                </>)
              }
            </Stack>
          </Grid>
        </Grid>
        {!!childrenResultCode && (
          <ErrorMessageCard
            messages={[translate(`memberModifyConfirmationChild.error.childrenResultCode${childrenResultCode}`)]}
          />
        )}
        <BottomButtonGroup>
          <ButtonOutlinedWarningL fullWidth type="submit">
            {translate('memberModifyChildDevelopment.button.decision')}
          </ButtonOutlinedWarningL>
          <BackButton />
        </BottomButtonGroup>
      </Stack>
    </Form>
  )
}

interface DevelopmentStageInputFormProps {
  categoryName: string
  categoryMst: GetCategoryMstDto
}

export const DevelopmentStageInputForm = (props: DevelopmentStageInputFormProps) => {
  const { categoryMst, categoryName } = props
  return (
    <>
      {categoryMst?.options && (
        categoryMst.options.map((value, index) => {
          return (
            <GItemContainer key={value.optionName} >
              <GItem xs={3} />
              <GItem xs={9} >
                <CheckBox 
                  name={`${categoryName}.${index}.flag`}
                  label={value.optionName}
                  valueAtCheck={yesNo.yes}
                >  
                {value.optionName}
                </CheckBox>
                {value.addQuestions && 
                  <AddQuestions_1 
                    topName={`${categoryName}.${index}`}
                    items={value.addQuestions}
                  />
                }
              </GItem>
            </GItemContainer>
          )
        })
      )}
    </>
  )
}

interface AddQuestionsProps {
  topName: string
  items: AddQuestion[]
}

/** 追加質問_1 */
export const AddQuestions_1 = (props: AddQuestionsProps) => {
  const { topName, items } = props
  
  const methods = useFormContext()
  const topFlagValue = useWatch({ name: `${topName}.flag`, control: methods.control })

  return (
    <>
      {topFlagValue === Flag.ON && (
        items.map((item) => {
          switch(item.fieldName){
            case "from":
              return (
                <div  key={item.questionText} style={{ marginLeft: '3rem' }}>
                  <GItemContainer xs={12} rowSpacing={1}>
                    <MiddleGItem xs={5} sm={2}>
                      {item.questionText}
                    </MiddleGItem>
                    <GItem xs={7} sm={10}>
                      <TextBoxAfterText 
                        label={item.questionText}
                        name={`${topName}.addQuestion.${item.fieldName}`}
                        afterText={translate('memberModifyChildDevelopment.label.month')}
                        maxLength={item.maxLength}
                        required={item.required === yesNo.yes}
                      />
                    </GItem>
                  </GItemContainer>
                </div>
              )
            default:
              return (
                <div  key={item.questionText} style={{ marginLeft: '3rem' }}>
                  <GItemContainer xs={12} rowSpacing={1}>
                    <MiddleGItem xs={5} sm={2}>
                      {item.questionText}
                    </MiddleGItem>
                    <GItem xs={7} sm={10}>
                      <TextBox  
                        label={item.questionText}
                        name={`${topName}.addQuestion.${item.fieldName}`}
                        maxLength={item.maxLength}
                        required={item.required === yesNo.yes}
                      />
                    </GItem>
                  </GItemContainer>
                </div>
              )
          }
        })
      )}
    </>
  )
}

interface ChangingClothesInputFormProps {
  categoryMst: GetCategoryMstDto
}
const ChangingClothesInputForm = (props: ChangingClothesInputFormProps) => {
  const { categoryMst } = props
  const existFlagName = "development.changingClothes"
  const methods = useFormContext()
  const existFlag = useWatch({ name: existFlagName ?? '', control: methods.control })
  if (categoryMst.options == null || categoryMst.options.length === 0) return null
  const labelData = categoryMst.options.map(op => ({ label: op.optionName, value: op.code }))

  return (
    <>
      <GItemContainer xs={12} rowSpacing={1}>
        <MiddleGItem xs={5} sm={2}>
          {categoryMst.required === yesNo.yes && (
            <AttentionLabel>*</AttentionLabel>
          )}
          {categoryMst.name}
        </MiddleGItem>
        <GItem xs={7} sm={10}>
          <RadioButtonGroup
            label={categoryMst.altCategoryName ?? categoryMst.name}
            name={existFlagName}
            buttonValueLabels={labelData}
            required={categoryMst.required === yesNo.yes}
            row
          />
        </GItem>
      </GItemContainer>
      {existFlag === '3' && categoryMst.options && (
        categoryMst.options.map((opt, index) => {
          return (
            <GItemContainer key={`${categoryMst.category}.${index}`}>
              <GItem xs={3} />
              <GItem xs={9} >
                {opt.addQuestions && 
                  <AddQuestions_2 
                    topName={categoryMst.category}
                    items={opt.addQuestions}
                  />
                }
              </GItem>
            </GItemContainer>
          )
        })
      )}
    </>
  )
}

/** 追加質問_2 */
const AddQuestions_2 = (props: AddQuestionsProps) => {
  const { items } = props;
  const options = ["development.assistTakeOff", "development.assistShirt", "development.assistPants", "development.assistButton"];

  return (
    <>
      {items && items.map((item) => (
        <div key={item.questionText} style={{ marginLeft: '3rem' }}>
          {item.questionText}
            {item.addQuestionOptions &&
              item.addQuestionOptions.map((op, index) => (
                <CheckBox
                  key={op.optionText}
                  name={options[index] || ""}
                  label={op.optionText}
                  valueAtCheck={yesNo.yes}
                >  
                {op.optionText}
                </CheckBox>
              ))
            }
        </div>
      ))}
    </>
  )
}

